/**
 * Per-template notification rule declarations (R5 + R5 in-flight extension).
 *
 * Each rule has a `phase` and a unified `resolve(ctx)` function that
 * returns a list of reminder occurrences. The materializer enumerates
 * each template's `notificationRules`, filters by the materialization
 * phase, and INSERTs one `contract_notifications` row per occurrence.
 *
 *   phase = 'post_signing' — materialized when status flips to `signed`.
 *                            resolve typically returns 3mo / 1mo / 1wk
 *                            leads before a lifecycle deadline. Use the
 *                            `postSigningRule()` helper.
 *
 *   phase = 'in_flight'    — materialized at send-for-signing
 *                            (status='sent'). Bell + dashboard auto-hide
 *                            these the moment the contract leaves
 *                            `{sent, viewed, awaiting_signature}` (no
 *                            auto-acknowledge — the row stays for
 *                            history). Use the `inFlightRule()` helper.
 *
 * Rules are channel-agnostic by design (see ADR-0031 / R5 plan
 * "Extending to email later"). The `ruleKey`, `kind`, and `leadLabel`
 * values are stable and will map 1:1 to future SES template names.
 * Don't introduce email-shaped names here.
 */
/**
 * Parse a `YYYY-MM-DD` (or ISO) string into a `Date`. Returns `null` for
 * empty / malformed input — resolvers use this to short-circuit when a
 * date field is missing.
 */
export function parseDateValue(raw) {
    if (typeof raw !== 'string')
        return null;
    const trimmed = raw.trim();
    if (!trimmed)
        return null;
    const d = new Date(trimmed.length === 10 ? `${trimmed}T00:00:00Z` : trimmed);
    if (Number.isNaN(d.getTime()))
        return null;
    return d;
}
/**
 * Coerce a number-shaped value (number OR numeric string) to a finite
 * `number`. Returns `null` when not coercible or non-finite.
 */
export function parseNumberValue(raw) {
    if (typeof raw === 'number' && Number.isFinite(raw))
        return raw;
    if (typeof raw === 'string' && raw.trim()) {
        const n = Number(raw);
        if (Number.isFinite(n))
            return n;
    }
    return null;
}
/** Subtract `months` calendar months from a UTC date. */
export function subtractMonths(d, months) {
    const out = new Date(d.getTime());
    out.setUTCMonth(out.getUTCMonth() - months);
    return out;
}
/** Add `months` calendar months to a UTC date. */
export function addMonths(d, months) {
    const out = new Date(d.getTime());
    out.setUTCMonth(out.getUTCMonth() + months);
    return out;
}
/** Subtract `days` from a UTC date. */
export function subtractDays(d, days) {
    const out = new Date(d.getTime());
    out.setUTCDate(out.getUTCDate() - days);
    return out;
}
/** Add `days` to a UTC date. */
export function addDays(d, days) {
    const out = new Date(d.getTime());
    out.setUTCDate(out.getUTCDate() + days);
    return out;
}
/** Map a non-custom lead label to a `fire_at` offset from `triggerAt`. */
export function computeFireAt(triggerAt, lead) {
    switch (lead) {
        case '3mo':
            return subtractMonths(triggerAt, 3);
        case '1mo':
            return subtractMonths(triggerAt, 1);
        case '1wk':
            return subtractDays(triggerAt, 7);
        case 'custom':
            return triggerAt;
    }
}
/** Default lead set for post-signing lifecycle rules. */
export const DEFAULT_LEADS = [
    '3mo',
    '1mo',
    '1wk',
];
/**
 * Helper for post-signing lifecycle rules. Resolves a deadline date
 * from `contract.values` and emits one occurrence per declared lead.
 * Returns zero occurrences when the deadline is unset / unresolvable
 * — that's the path Bostadshyra Tillsvidare takes to yield no rows.
 */
export function postSigningRule(opts) {
    const leads = opts.leads ?? DEFAULT_LEADS;
    return {
        ruleKey: opts.ruleKey,
        kind: opts.kind,
        label: opts.label,
        phase: 'post_signing',
        resolve: (ctx) => {
            const triggerAt = opts.resolveTriggerAt(ctx.values);
            if (!triggerAt)
                return [];
            return leads.map((lead) => ({
                triggerAt,
                fireAt: computeFireAt(triggerAt, lead),
                leadLabel: lead,
                ...(opts.occurrenceLabel ? { label: opts.occurrenceLabel } : {}),
            }));
        },
    };
}
/**
 * Helper for in-flight follow-up rules: emits one occurrence per entry
 * in `daysAfterSent`, each scheduled that many days after the contract
 * was sent for signing. Returns zero occurrences when `sentAt` is unset
 * — i.e. the materializer is being called outside the send-for-signing
 * path, which is a no-op for in-flight rules.
 *
 * Use `perOccurrenceLabels` to give each escalation step its own user-
 * facing wording (e.g. "Kontakta hyresgästen" vs "Hänskjut till
 * hyresnämnden"). The position in `perOccurrenceLabels` matches the
 * position in `daysAfterSent`.
 */
export function inFlightRule(opts) {
    return {
        ruleKey: opts.ruleKey,
        kind: opts.kind,
        label: opts.label,
        phase: 'in_flight',
        resolve: (ctx) => {
            if (!ctx.sentAt)
                return [];
            const sentAt = ctx.sentAt;
            return opts.daysAfterSent.map((days, i) => {
                const label = opts.perOccurrenceLabels?.[i];
                return {
                    triggerAt: sentAt,
                    fireAt: addDays(sentAt, days),
                    leadLabel: 'custom',
                    ...(label ? { label } : {}),
                };
            });
        },
    };
}
/**
 * Stable ruleKey of the default signing monitor. The materializer
 * injects this rule into *every* contract's in-flight set (not just
 * templates that declare one) so any Scrive send gets deadline
 * follow-up reminders in the landlord's bell. Templates must not
 * declare an in-flight rule with this key — the materializer dedupes
 * in the template's favour, which would silence the default.
 */
export const SIGNING_MONITOR_RULE_KEY = 'signing_due_followup';
/**
 * Default, template-agnostic in-flight monitor anchored on the signing
 * **due date** (`ctx.dueAt`). Scrive's own automatic reminder
 * (`days_to_remind`) nudges the *signer* early; this rule nudges the
 * *landlord* in-app as the deadline nears, so an unresolved signing
 * doesn't fall through the cracks. Re-materialized on `signing.prolong`
 * so the reminders move with the new deadline.
 *
 * Emits up to two occurrences — 7 days and 1 day before the due date.
 * `retainOccurrences` drops any that have already passed and, for a
 * still-future deadline whose leads have all slipped, rescues the most
 * recent one so a short signing window still surfaces in bevakning.
 * Yields nothing when the contract was sent without a deadline.
 */
export const SIGNING_MONITOR_RULE = {
    ruleKey: SIGNING_MONITOR_RULE_KEY,
    kind: 'signature_overdue',
    label: 'Avtalet är inte signerat — följ upp',
    phase: 'in_flight',
    resolve: (ctx) => {
        const dueAt = ctx.dueAt;
        if (!dueAt)
            return [];
        const leads = [
            {
                days: 7,
                label: 'Avtalet är inte signerat — påminn hyresgästen (7 dagar kvar till förfallodag)',
            },
            {
                days: 1,
                label: 'Signeringsfristen går ut imorgon — följ upp eller förläng',
            },
        ];
        return leads.map(({ days, label }) => ({
            triggerAt: dueAt,
            fireAt: subtractDays(dueAt, days),
            leadLabel: 'custom',
            label,
        }));
    },
};
//# sourceMappingURL=notification-rules.js.map